import apiClient from "../../../shared/services/apiClient";
import { upsertEegChannelCatalog } from "../../../shared/services/eegChannelCatalog";
import {
  Channel,
  Montage,
  MontageVariant,
  ChannelPairValidation,
} from "../types/montage.types";

const CHANNEL_API_BASE = "/eeg/channels";
const MONTAGE_API_BASE = "/montages";
const MONTAGE_VARIANT_API_BASE = "/montage-variants";
const MONTAGE_TYPES = new Set<Montage["montage_type"]>([
  "bipolar",
  "referential",
  "laplacian",
  "average_reference",
  "passthrough",
]);

function normalizeEtagValue(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const token = value.trim();
  return token.length > 0 ? token : null;
}

function normalizeLockVersion(value: unknown): string | null {
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return String(Math.trunc(value));
  }
  if (typeof value === "bigint" && value >= 0n) {
    return value.toString();
  }
  return null;
}

function unwrapPayload<T>(payload: unknown): T | null {
  if (payload && typeof payload === "object" && "data" in (payload as any)) {
    return ((payload as any).data as T) ?? null;
  }
  return (payload as T) ?? null;
}

function normalizeChannelPairs(value: unknown): Montage["channel_pairs"] {
  let source = value;
  if (typeof source === "string") {
    try {
      source = JSON.parse(source);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(source)) {
    return [];
  }

  return source
    .map((entry) => {
      const item = entry as Record<string, unknown>;
      const positive =
        typeof item?.positive === "string"
          ? item.positive
          : typeof item?.positive_code === "string"
            ? item.positive_code
            : typeof item?.source === "string"
              ? item.source
              : "";
      const negative =
        typeof item?.negative === "string"
          ? item.negative
          : typeof item?.negative_code === "string"
            ? item.negative_code
            : typeof item?.target === "string"
              ? item.target
              : "";
      const label =
        typeof item?.label === "string"
          ? item.label
          : positive
            ? [positive, negative].filter(Boolean).join("-")
            : undefined;

      return {
        positive,
        negative,
        label,
        region: typeof item?.region === "string" ? item.region : undefined,
        color: typeof item?.color === "string" ? item.color : undefined,
        visible:
          typeof item?.visible === "boolean" ? item.visible : undefined,
        special:
          typeof item?.special === "boolean" ? item.special : undefined,
        traceSettings:
          item?.traceSettings && typeof item.traceSettings === "object"
            ? (item.traceSettings as Montage["channel_pairs"][number]["traceSettings"])
            : item?.trace_settings && typeof item.trace_settings === "object"
              ? (item.trace_settings as Montage["channel_pairs"][number]["traceSettings"])
              : undefined,
      };
    })
    .filter((pair) => pair.positive.length > 0);
}

function normalizeMontageType(value: unknown): Montage["montage_type"] {
  const candidate =
    typeof value === "string" ? value.trim().toLowerCase() : "bipolar";
  return MONTAGE_TYPES.has(candidate as Montage["montage_type"])
    ? (candidate as Montage["montage_type"])
    : "bipolar";
}

export function normalizeMontage(raw: unknown): Montage {
  const source = (raw ?? {}) as Record<string, unknown>;
  const lockVersion = normalizeLockVersion(
    source.lock_version ?? source.lockVersion,
  );
  return {
    montage_id:
      typeof source.montage_id === "string"
        ? source.montage_id
        : typeof source.id === "string"
          ? source.id
          : undefined,
    montage_name:
      typeof source.montage_name === "string"
        ? source.montage_name
        : typeof source.name === "string"
          ? source.name
          : typeof source.title === "string"
            ? source.title
            : "Untitled Montage",
    montage_type: normalizeMontageType(
      source.montage_type ?? source.type ?? source.montageType,
    ),
    channel_pairs: normalizeChannelPairs(
      source.channel_pairs ?? source.channelPairs,
    ),
    ...(lockVersion !== null ? { lock_version: lockVersion } : {}),
    description:
      typeof source.description === "string"
        ? source.description
        : typeof source.summary === "string"
          ? source.summary
          : undefined,
    is_default:
      source.is_default !== undefined
        ? Boolean(source.is_default)
        : source.is_system_default !== undefined
          ? Boolean(source.is_system_default)
          : source.isDefault !== undefined
            ? Boolean(source.isDefault)
            : source.isSystemDefault !== undefined
              ? Boolean(source.isSystemDefault)
              : false,
    created_by:
      typeof source.created_by === "string"
        ? source.created_by
        : typeof source.createdBy === "string"
          ? source.createdBy
          : typeof source.owner_id === "string"
            ? source.owner_id
            : typeof source.ownerId === "string"
              ? source.ownerId
              : undefined,
    is_shared:
      source.is_shared !== undefined
        ? Boolean(source.is_shared)
        : source.isShared !== undefined
          ? Boolean(source.isShared)
          : false,
    created_at:
      typeof source.created_at === "string"
        ? source.created_at
        : typeof source.createdAt === "string"
          ? source.createdAt
          : undefined,
    updated_at:
      typeof source.updated_at === "string"
        ? source.updated_at
        : typeof source.updatedAt === "string"
          ? source.updatedAt
          : undefined,
  };
}

export function normalizeMontageArrayPayload(payload: unknown): Montage[] {
  const raw = unwrapPayload<unknown[]>(payload);
  return Array.isArray(raw) ? raw.map(normalizeMontage) : [];
}

export function normalizeMontageRecordPayload(payload: unknown): Montage {
  return normalizeMontage(unwrapPayload(payload));
}

export type MontageWithMeta = {
  montage: Montage;
  etag: string | null;
  lockVersion: string | null;
};

/**
 * Fetch all channels
 */
export const fetchChannels = async (filters?: {
  electrode_system?: string;
  region?: string;
  hemisphere?: string;
  is_standard?: boolean;
}): Promise<Channel[]> => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        if (key === "electrode_system") {
          // Backend route supports `system` as canonical query key.
          params.append("system", String(value));
        } else {
          params.append(key, String(value));
        }
      }
    });
  }

  const response = await apiClient.get<{ success: boolean; data: Channel[] }>(
    `${CHANNEL_API_BASE}?${params.toString()}`,
  );
  const channels = response.data.data || [];
  upsertEegChannelCatalog(channels);
  return channels;
};

export const fetchElectrodeSystems = async () => {
  const response = await apiClient.get(`${CHANNEL_API_BASE}/systems`);
  return response.data.data;
};

export const fetchElectrodeSystemAccess = async (): Promise<{ can_manage_org_eeg_config: boolean; is_platform_admin: boolean }> => {
  const response = await apiClient.get(`${CHANNEL_API_BASE}/systems/access`);
  return response.data.data;
};

export const createElectrodeSystem = async (input: { system_code: string; system_name: string }) => {
  const response = await apiClient.post(`${CHANNEL_API_BASE}/systems`, input);
  return response.data.data;
};

export const cloneElectrodeSystem = async (id: string, input: { system_code: string; system_name: string }) => {
  const response = await apiClient.post(`${CHANNEL_API_BASE}/systems/${id}/clone`, input);
  return response.data.data;
};

export const archiveElectrodeSystem = async (id: string) => {
  await apiClient.delete(`${CHANNEL_API_BASE}/systems/${id}`);
};

/**
 * Fetch channels by electrode system
 */
export const fetchChannelsBySystem = async (
  system: string,
): Promise<Channel[]> => {
  const response = await apiClient.get<{ success: boolean; data: Channel[] }>(
    `${CHANNEL_API_BASE}/system/${system}`,
  );
  const channels = response.data.data || [];
  upsertEegChannelCatalog(channels);
  return channels;
};

/**
 * Fetch channels by brain region
 */
export const fetchChannelsByRegion = async (
  region: string,
): Promise<Channel[]> => {
  const response = await apiClient.get<{ success: boolean; data: Channel[] }>(
    `${CHANNEL_API_BASE}/region/${region}`,
  );
  const channels = response.data.data || [];
  upsertEegChannelCatalog(channels);
  return channels;
};

/**
 * Fetch channel by code
 */
export const fetchChannelByCode = async (code: string): Promise<Channel> => {
  const response = await apiClient.get<{ success: boolean; data: Channel }>(
    `${CHANNEL_API_BASE}/code/${code}`,
  );
  if (response.data.data) {
    upsertEegChannelCatalog([response.data.data]);
  }
  return response.data.data;
};

export const createChannel = async (
  channel: Partial<Channel>,
): Promise<Channel> => {
  const response = await apiClient.post<{ success: boolean; data: Channel }>(
    CHANNEL_API_BASE,
    channel,
  );
  if (response.data.data) {
    upsertEegChannelCatalog([response.data.data]);
  }
  return response.data.data;
};

export const updateChannel = async (
  id: string,
  channel: Partial<Channel>,
): Promise<Channel> => {
  const response = await apiClient.put<{ success: boolean; data: Channel }>(
    `${CHANNEL_API_BASE}/${id}`,
    channel,
  );
  if (response.data.data) {
    upsertEegChannelCatalog([response.data.data]);
  }
  return response.data.data;
};

/**
 * Validate channel code
 */
export const validateChannelCode = async (
  channelCode: string,
): Promise<boolean> => {
  const response = await apiClient.post<{
    success: boolean;
    is_valid: boolean;
  }>(`${CHANNEL_API_BASE}/validate/code`, { channel_code: channelCode });
  return response.data.is_valid;
};

/**
 * Validate channel pair for montage
 */
export const validateChannelPair = async (
  positiveCode: string,
  negativeCode: string,
): Promise<ChannelPairValidation> => {
  const response = await apiClient.post<{
    success: boolean;
    data: ChannelPairValidation;
  }>(`${CHANNEL_API_BASE}/validate/pair`, {
    positive_code: positiveCode,
    negative_code: negativeCode,
  });
  return response.data.data;
};

/**
 * Fetch all montages
 */
export const fetchMontages = async (): Promise<Montage[]> => {
  const response = await apiClient.get<{ success: boolean; data: Montage[] }>(
    MONTAGE_API_BASE,
  );
  return normalizeMontageArrayPayload(response.data);
};

/**
 * Fetch montage by ID
 */
export const fetchMontageById = async (id: string): Promise<Montage> => {
  const response = await apiClient.get<{ success: boolean; data: Montage }>(
    `${MONTAGE_API_BASE}/${id}`,
  );
  return normalizeMontageRecordPayload(response.data);
};

export const fetchMontageByIdWithMeta = async (
  id: string,
): Promise<MontageWithMeta> => {
  const response = await apiClient.get<{ success: boolean; data: Montage }>(
    `${MONTAGE_API_BASE}/${id}`,
  );
  const montage = normalizeMontageRecordPayload(response.data);
  const etag = normalizeEtagValue(response.headers?.etag);
  const lockVersion = normalizeLockVersion((montage as any)?.lock_version);
  return {
    montage,
    etag,
    lockVersion,
  };
};

/**
 * Create new montage
 */
export const createMontage = async (
  montage: Partial<Montage>,
): Promise<Montage> => {
  const response = await apiClient.post<{ success: boolean; data: Montage }>(
    MONTAGE_API_BASE,
    montage,
  );
  return normalizeMontageRecordPayload(response.data);
};

/**
 * Update montage
 */
export const updateMontage = async (
  id: string,
  montage: Partial<Montage>,
  options?: {
    ifMatch?: string | null;
    expectedVersion?: string | number | bigint | null;
  },
): Promise<Montage> => {
  const payload: Record<string, unknown> = { ...montage };
  const expectedVersion = normalizeLockVersion(options?.expectedVersion);
  if (expectedVersion !== null) {
    payload.expected_version = expectedVersion;
  }

  const headers: Record<string, string> = {};
  if (options?.ifMatch) {
    headers["If-Match"] = options.ifMatch;
  }

  const response = await apiClient.put<{ success: boolean; data: Montage }>(
    `${MONTAGE_API_BASE}/${id}`,
    payload,
    { headers },
  );
  return normalizeMontageRecordPayload(response.data);
};

/**
 * Delete montage
 */
export const deleteMontage = async (id: string): Promise<void> => {
  await apiClient.delete(`${MONTAGE_API_BASE}/${id}`);
};

/**
 * Get suggested channel pairs for montage type
 */
export const getSuggestedChannelPairs = async (
  montageType: "bipolar" | "referential" | "laplacian",
  electrodeSystem: "10-20" | "10-10" = "10-20",
): Promise<{ positive: string; negative: string; label: string }[]> => {
  const response = await apiClient.get<{
    success: boolean;
    data: { positive: string; negative: string; label: string }[];
  }>(
    `${CHANNEL_API_BASE}/suggested?montage_type=${encodeURIComponent(montageType)}&system=${encodeURIComponent(electrodeSystem)}`,
  );
  return Array.isArray(response.data.data) ? response.data.data : [];
};

// ── EEG-CUX-007: Montage Variant API ─────────────────────────────────────────

export const fetchMontageVariants = async (
  baseMontageId?: string,
): Promise<MontageVariant[]> => {
  const url = baseMontageId
    ? `${MONTAGE_VARIANT_API_BASE}?base_montage_id=${encodeURIComponent(baseMontageId)}`
    : MONTAGE_VARIANT_API_BASE;
  const response = await apiClient.get<{
    success: boolean;
    data: MontageVariant[];
  }>(url);
  return Array.isArray(response.data?.data) ? response.data.data : [];
};

export const createMontageVariant = async (payload: {
  base_montage_id: string;
  name: string;
  excluded_channels: string[];
}): Promise<MontageVariant> => {
  const response = await apiClient.post<{
    success: boolean;
    data: MontageVariant;
  }>(MONTAGE_VARIANT_API_BASE, payload);
  return response.data.data;
};

export const renameMontageVariant = async (
  variantId: string,
  name: string,
): Promise<MontageVariant> => {
  const response = await apiClient.patch<{
    success: boolean;
    data: MontageVariant;
  }>(`${MONTAGE_VARIANT_API_BASE}/${variantId}`, { name });
  return response.data.data;
};

export const deleteMontageVariant = async (
  variantId: string,
): Promise<void> => {
  await apiClient.delete(`${MONTAGE_VARIANT_API_BASE}/${variantId}`);
};
